const LINE_BREAKS_REGEX = /[\r\n\u2028\u2029]+/g;

const getBrowserSelection = (): Selection | null => {
    if (typeof window === 'undefined' || typeof window.getSelection === 'undefined') return null;
    return window.getSelection();
};

/**
 * Standard value for plain text contentEditable fields.
 */
export const CONTENT_EDITABLE_PLAINTEXT_ONLY = 'plaintext-only';

/**
 * Focuses a contentEditable element and places the caret at the end of its content.
 */
export const placeCaretAtEnd = (el: HTMLElement): void => {
    el.focus();
    if (typeof document === 'undefined' || typeof document.createRange === 'undefined') return;

    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);

    const selection = getBrowserSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
};

/**
 * Focuses a contentEditable element and selects all of its text content.
 */
export const selectAll = (el: HTMLElement): void => {
    el.focus();
    if (typeof document === 'undefined' || typeof document.createRange === 'undefined') return;

    const range = document.createRange();
    range.selectNodeContents(el);

    const selection = getBrowserSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
};

/**
 * Normalizes text for single-line contentEditable fields.
 */
export const normalizeToSingleLinePlainText = (text: string): string => text.replace(LINE_BREAKS_REGEX, ' ');

/**
 * Trims inserted text so resulting content does not exceed maxLength.
 */
export const trimTextToMaxLengthAtSelection = ({
    text,
    root,
    maxLength,
}: {
    text: string;
    root: HTMLElement | null;
    maxLength?: number;
}): string => {
    if (maxLength === undefined || !root) return text;

    const selection = getBrowserSelection();
    const selectedLength =
        selection &&
        selection.rangeCount > 0 &&
        root.contains(selection.getRangeAt(0).commonAncestorContainer) &&
        selection.toString()
            ? selection.toString().length
            : 0;

    const current = root.innerText ?? '';
    const remainingLength = maxLength - (current.length - selectedLength);
    if (remainingLength <= 0) return '';

    return text.slice(0, remainingLength);
};

const appendTextToRoot = (root: HTMLElement, text: string): void => {
    root.textContent = `${root.textContent ?? ''}${text}`;
};

/**
 * Inserts plain text into a contentEditable root at current selection.
 */
export const insertTextAtSelection = ({ root, text }: { root: HTMLElement | null; text: string }): void => {
    if (!text || !root) return;

    const selection = getBrowserSelection();
    if (!selection || selection.rangeCount === 0) {
        appendTextToRoot(root, text);
        return;
    }

    const range = selection.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) {
        appendTextToRoot(root, text);
        return;
    }

    if (typeof document === 'undefined' || typeof document.createTextNode === 'undefined') {
        appendTextToRoot(root, text);
        return;
    }

    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
};
